const { Core, Utils, Events, Cache } = Global;
const { $loading } = Utils;

customElements.define('collection-provider', class extends Core {

    elements = {
        $: [['collection-form'], 'loader']
    }

    propTypes = {
        'collection-url': String
    }

    render() {
        this._getForms();
        this.sub(Events.COLLECTION_CHANGE, this._handleChange);
        this._initCache();
        this.onHistory = () => this._handleChange({ src: 'history', url: window.location.href });
        window.addEventListener('popstate', this.onHistory);
    }

    _getForms() {
        this.$forms = new Map();
        this.querySelectorAll('[data-element="collection-form"]').forEach((form) => {
            this.$forms.set(form.id, form);
        });
    }
    
    async _handleChange({ src, url, formId }) {
        const requestId = this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();
        this.controller = new AbortController();
        this.src = src;
        this._getUrlParams(url, formId);
        const params = new URLSearchParams(this.urlParams);
        const key = this._cacheKey(params);
        const loader = this.$('loader');
        $loading(loader, this.withLoading);
        this.setAttribute('aria-busy', 'true');
        try {
            const doc = Cache.get(key) || await this._fetchDoc(params, this.controller.signal);
            if (requestId !== this.requestId || !this.isConnected) return;
            this._remember(key, doc);
            this.$doc = doc;
            this.urlParams = params;
            if (src !== 'history') this._updateHistory();
            this.pub(Events.COLLECTION_UPDATED, { src, doc });
            this._getForms();
        } catch (error) {
            if (error.name !== 'AbortError' && requestId === this.requestId) {
                // A normal navigation is safer than leaving filters and results
                // disagreeing when the section endpoint fails.
                params.delete('section_id');
                const fallback = new URL(this.prop('collection-url'), window.location.origin);
                fallback.search = params.toString();
                window.location.assign(fallback.href);
            }
        } finally {
            if (requestId === this.requestId) {
                $loading(loader, false);
                this.removeAttribute('aria-busy');
            }
        }
    }

    set src(value) {
        const inert = (value === 'dynamic-pagination');
        this.withLoading = !inert;
        if(!inert) {
            this._scrollToTop();
        }
    }

    _initCache() {
        this.cacheKeys = [];
        this._remember(this._cacheKey(new URLSearchParams(window.location.search)),
            Utils.parseHTML(this.$section.outerHTML));
    }

    _cacheKey(params) {
        const normalized = new URLSearchParams(params);
        normalized.delete('section_id');
        normalized.sort();
        return `collection:${this.prop('collection-url')}:${this.sectionId}:${normalized}`;
    }

    _remember(key, doc) {
        Cache.set(key, doc);
        this.cacheKeys = this.cacheKeys.filter(existing => existing !== key);
        this.cacheKeys.push(key);
        if (this.cacheKeys.length > 12) Cache.delete(this.cacheKeys.shift());
    }

    _getUrlParams(url, formId) {
        if(!url && !formId) {
            console.error('collection change does not contain resource');
            return;
        }
        this.urlParams = url
            ? new URL(url, window.location.origin).searchParams
            : new URLSearchParams(this._formData(formId));
    }

    _updateHistory() {
        this.urlParams.delete('section_id');
        window.history.pushState({}, null, `${window.location.pathname}?${this.urlParams.toString()}`);
    }

    _formData(formId) {
        if(!this.$forms.has(formId)) {
            console.error(`form not found with id ${formId}`);
            return;
        }
        return new FormData(this.$forms.get(formId));
    }

    async _fetchDoc(params, signal) {
        const url = new URL(this.prop('collection-url'), window.location.origin);
        url.search = new URLSearchParams(params).toString();
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error(`Collection failed (${response.status})`);
        const doc = Utils.parseHTML(await response.text());
        if (!doc.querySelector('collection-provider')) throw new Error('Missing collection section');
        return doc;
    }

    destroy() {
        this.requestId = (this.requestId || 0) + 1;
        this.controller?.abort();
        window.removeEventListener('popstate', this.onHistory);
        this.cacheKeys?.forEach(key => Cache.delete(key));
    }

    _scrollToTop() {
        window.scrollTo({
            top: this.topPoint + window.pageYOffset,
            behavior: 'smooth',
        });
    }

    get topPoint() {
        const headerHeight = document.documentElement.style.getPropertyValue('--header-height')?.replace('px', '');
        const offset = this.getBoundingClientRect().top;
        return headerHeight ? offset - headerHeight : offset;
    }
})
